<template>
  <div>
    <ItemRow
      ref="root"
      :class="$style.itemRow"
    >
      <ItemWithLabel
        v-for="(column, index) in columns"
        :key="index"
        :class="[$style.item, `${$style.item}--${column.dataFieldName}`]"
        :column="column"
        :data-test-id="`CloudInsightsItemRow__${column.dataFieldName}`"
      >
        <button
          v-if="showExpandButtonColumn(column)"
          :class="$style.expandButton"
          :data-test-id="$testId('expandButton')"
          @click="handleExpandButtonClick"
        >
          <CollapseCaret
            :class="$style.caret"
            :collapsed="!isExpanded"
          />
        </button>
        <span
          v-else-if="showDateTimeColumn(column)"
          :data-test-id="$testId('eventDate')"
        >
          {{ eventDate }}
          <span :class="$style.subText">
            {{ eventTime }}
          </span>
        </span>
        <span
          v-else-if="showConnectorColumn(column)"
          :class="$style.connector"
          :data-test-id="$testId('connector')"
        >
          {{ connectorValue }}
        </span>
        <span
          v-else-if="showIdentityColumn(column)"
          :class="$style.mainText"
          :data-test-id="$testId('identity')"
        >
          {{ identityMain }}
          <span :class="$style.subText">
            {{ identitySub }}
          </span>
        </span>
        <span
          v-else-if="showRequestParametersColumn(column)"
          :data-test-id="$testId('requestParameters')"
        >
          <pre
            :class="$style.jsonInline"
          >{{ requestParameters }}</pre>
        </span>
        <span
          v-else-if="showResourceArnColumn(column)"
          :class="$style.mainText"
          :data-test-id="$testId('resourceArn')"
        >
          {{ resourceArn }}
          <span :class="$style.subText">
            {{ resourceTypeSub }}
          </span>
        </span>
        <span
          v-else-if="showResourceAccountIdColumn(column)"
          :data-test-id="$testId('resourceAccountId')"
        >
          {{ resourceAccountId }}
        </span>
        <span
          v-else-if="showResourceTypeColumn(column)"
          :data-test-id="$testId('resourceType')"
        >
          {{ resourceType }}
        </span>
        <span
          v-else-if="showResponseElementsColumn(column)"
          :data-test-id="$testId('responseElements')"
        >
          <pre
            :class="$style.jsonInline"
          >{{ responseElements }}</pre>
        </span>
        <span
          v-else-if="showSessionIssuerColumn(column)"
          :class="$style.mainText"
          :data-test-id="$testId('sessionIssuer')"
        >
          {{ sessionIssuerMain }}
          <span :class="$style.subText">
            {{ sessionIssuerSub }}
          </span>
        </span>
        <span
          v-else-if="showNonPersonEntityColumn(column)"
          :data-test-id="$testId('nonPersonEntity')"
        >
          {{ nonPersonEntity }}
        </span>
        <span
          v-else-if="showActionColumn(column)"
          :class="$style.mainText"
          :data-test-id="$testId('action')"
        >
          {{ actionMain }}
          <span :class="$style.subText">
            {{ actionSub }}
          </span>
        </span>
        <span
          v-else
          :class="$style.valueText"
        >
          {{ getDataValue(column.dataFieldName) }}
        </span>
      </ItemWithLabel>
    </ItemRow>
    <ItemRow
      v-if="isExpanded"
      :class="$style.eventBody"
      :data-test-id="$testId('eventBody')"
    >
      <div :class="$style.eventDetails">
        <ul :class="$style.nav">
          <li
            v-for="tab in [$options.tabs.json]"
            :key="tab"
          >
            <a
              :class="[$style.tabLink, {
                [$style.tabLinkActive]: shouldShowTab(tab),
              }]"
            >
              {{ tab }}
            </a>
          </li>
        </ul>
        <div
          :class="$style.contentContainer"
          :data-test-id="$testId('jsonContent')"
        >
          <pre
            v-show="shouldShowTab($options.tabs.json)"
            :class="$style.json"
          >{{ itemJson }}</pre>
        </div>
      </div>
    </ItemRow>
  </div>
</template>

<script>
import {
  ItemRow,
  ItemWithLabel,
} from '@jumpcloud/ui-components';
import { mixin as clickaway } from 'vue-clickaway';
import { cloudInsightsColumns, flattenObject } from 'client/CloudInsights/constants/CloudInsightsConstants';
import CloudInsightsUtil from 'client/CloudInsights/util/CloudInsightsUtil';
import CollapseCaret from 'client/components/ContentSection/CollapseCaret.vue';
import Constants from 'client/util/Constants';
import moment from 'moment';

const { dataNotAvailable } = Constants;

const tabs = {
  json: 'JSON',
  summary: 'Summary',
};

export default {
  name: 'CloudInsightsItemRow',

  components: {
    CollapseCaret,
    ItemRow,
    ItemWithLabel,
  },

  tabs,

  mixins: [clickaway],

  props: {
    columns: {
      type: Array,
      default: () => ([]),
    },

    itemData: {
      required: true,
      type: Object,
    },
  },

  data: () => ({
    isExpanded: false,
  }),

  computed: {
    actionMain() {
      return this.itemData.eventName
        ? this.itemData.eventName
        : dataNotAvailable;
    },

    actionSub() {
      return this.itemData.event?.eventSource
        ? this.itemData.event.eventSource
        : dataNotAvailable;
    },

    connectorValue() {
      let connectorDisplayValue = dataNotAvailable;
      if (this.itemData.jumpcloudUser) {
        connectorDisplayValue = 'IAM';
      } else if (this.itemData.ssoUser) {
        connectorDisplayValue = 'SSO';
      }
      return connectorDisplayValue;
    },

    eventDate() {
      return moment.unix(this.itemData.event.eventTime.seconds).format('MM/DD/YY');
    },

    eventTime() {
      return moment.unix(this.itemData.event.eventTime.seconds).format('HH:mm:ss');
    },

    identityMain() {
      const { userIdentity } = this.itemData.event;
      let displayPrincipalId;

      if (userIdentity.type === 'AWSService') {
        return userIdentity.invokedBy || dataNotAvailable;
      }

      if (userIdentity.principalId) {
        displayPrincipalId = userIdentity.principalId.split(':').at(-1) || userIdentity.principalId;
      }

      return userIdentity.userName
      || userIdentity.sessionContext?.sessionIssuer?.userName
      || displayPrincipalId
      || userIdentity.arn
      || dataNotAvailable;
    },

    identitySub() {
      let substring = this.itemData.event.userIdentity.type;

      if (substring === 'AWSService') {
        substring = `${substring} | invokedBy`;
      } else if (this.itemData.event.userIdentity.userName) {
        substring = `${substring} | Username`;
      } else if (this.itemData.event.userIdentity.sessionContext?.sessionIssuer?.userName) {
        substring = `${substring} | Session Issuer`;
      } else if (this.itemData.event.userIdentity.principalId) {
        substring = `${substring} | Principal ID`;
      } else if (this.itemData.event.userIdentity.arn) {
        substring = `${substring} | ARN`;
      }
      return substring;
    },

    itemJson() {
      return CloudInsightsUtil.sanitizeForDisplay(this.itemData);
    },

    nonPersonEntity() {
      const { nonPersonEntity } = this.itemData;
      return nonPersonEntity ? 'true' : 'false';
    },

    requestParameters() {
      return JSON.stringify(this.itemData.event.requestParameters, null, ' ');
    },

    resourceAccountId() {
      const { resources } = this.itemData.event;

      if (!resources.length) return dataNotAvailable;

      let resourceAccountId = resources[0].accountId;
      if (resources.length > 1) {
        resourceAccountId = `${resourceAccountId} (multiple)`;
      }

      return resourceAccountId;
    },

    resourceArn() {
      const { resources } = this.itemData.event;

      if (!resources.length) return dataNotAvailable;

      let resourceArn = resources[0].ARN || resources[0].arn;
      if (resources.length > 1) {
        resourceArn = `${resourceArn} (multiple)`;
      }

      return resourceArn;
    },

    resourceType() {
      const { resources } = this.itemData.event;

      if (!resources.length) return dataNotAvailable;

      let resourceType = resources[0].type;
      if (!resourceType) return dataNotAvailable;

      if (resources.length > 1) {
        resourceType = `${resourceType} (multiple)`;
      }

      return resourceType;
    },

    resourceTypeSub() {
      const { resources } = this.itemData.event;

      if (!resources.length) return dataNotAvailable;

      return resources[0].type || dataNotAvailable;
    },

    responseElements() {
      return JSON.stringify(this.itemData.event.responseElements, null, ' ');
    },

    sessionIssuerMain() {
      const { sessionContext } = this.itemData.event.userIdentity;

      if (sessionContext && sessionContext.sessionIssuer) {
        return sessionContext.sessionIssuer.userName
      || sessionContext.sessionIssuer.principalId
      || sessionContext.sessionIssuer.arn
      || dataNotAvailable;
      }

      return dataNotAvailable;
    },

    sessionIssuerSub() {
      const { sessionContext } = this.itemData.event.userIdentity;

      if (sessionContext && sessionContext.sessionIssuer) {
        let substring = sessionContext.sessionIssuer.type;

        if (sessionContext.sessionIssuer.userName) {
          substring = `${substring} | Username`;
        } else if (sessionContext.sessionIssuer.principalId) {
          substring = `${substring} | Principal ID`;
        } else if (sessionContext.sessionIssuer.arn) {
          substring = `${substring} | ARN`;
        }
        return substring;
      }
      return '';
    },
  },

  created() {
    this.cloudInsightsCols = cloudInsightsColumns;
    this.itemDataFlatten = flattenObject(this.itemData);
  },

  methods: {
    getDataValue(dataFieldName) {
      return this.itemDataFlatten[dataFieldName] || dataNotAvailable;
    },

    handleExpandButtonClick() {
      this.isExpanded = !this.isExpanded;
    },

    shouldShowTab(tabName) {
      // Default to json view
      return tabName === this.$options.tabs.json;
    },

    showActionColumn(column) {
      return column.dataFieldName === this.cloudInsightsCols.eventName;
    },

    showConnectorColumn(column) {
      return column.dataFieldName === this.cloudInsightsCols.connector
        && this.connectorValue;
    },

    showDateTimeColumn(column) {
      return column.dataFieldName === this.cloudInsightsCols.eventTime;
    },

    showExpandButtonColumn(column) {
      return column.dataFieldName === this.cloudInsightsCols.expandButton;
    },

    showIdentityColumn(column) {
      return column.dataFieldName === this.cloudInsightsCols.identity;
    },

    showNonPersonEntityColumn(column) {
      return column.dataFieldName === this.cloudInsightsCols.nonPersonEntity;
    },

    showRequestParametersColumn(column) {
      return column.dataFieldName === this.cloudInsightsCols.event.requestParameters
        && JSON.parse(this.requestParameters);
    },

    showResourceAccountIdColumn(column) {
      return column.dataFieldName === this.cloudInsightsCols.event.resourcesAccountId;
    },

    showResourceArnColumn(column) {
      return column.dataFieldName === this.cloudInsightsCols.event.resourcesArn;
    },

    showResourceTypeColumn(column) {
      return column.dataFieldName === this.cloudInsightsCols.event.resourcesType;
    },

    showResponseElementsColumn(column) {
      return column.dataFieldName === this.cloudInsightsCols.event.responseElements
       && JSON.parse(this.responseElements);
    },

    showSessionIssuerColumn(column) {
      const { sessionIssuer } = this.cloudInsightsCols.event.userIdentity.sessionContext;
      return column.dataFieldName === sessionIssuer;
    },
  },
};
</script>

<style scoped module>
@value (jcGrey600) from '~css/brand-colors.css';
@value (gradientDark2, gradientDark4, gradientLight2, gradientLight3) from '~css/colors.css';

.caret {
  color: gradientDark4;
}

.connector {
  background: gradientLight3;
  border-radius: 4px;
  display: flex;
  flex-direction: column;
  height: 24px;
  justify-content: center;
  text-align: center;
  width: 60px;
}

.expandButton {
  background: white;
  cursor: pointer;
  flex: 0 0 auto;
  padding: 0 0 0 20px;
}

.subText {
  color: jcGrey600;
  display: block;
  font-size: small;
  padding-top: 5px;
}

.mainText {
  white-space: nowrap;
}

.itemRow {
  border-bottom: 1px solid var(--jc-border-color);
}

.itemRow /deep/ div:first-child {
  align-items: center;
}

.valueText {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.eventBody {
  border-bottom: 1px solid var(--jc-border-color);
}

.eventDetails {
  padding: 20px 30px;
}

.nav {
  margin: 0 !important; /* overriding legacy <ul> styles */
  padding: 0;
}

.nav li {
  display: inline-block;
  list-style-type: none;
}

.tabLink {
  color: gradientDark2;
  cursor: pointer;
  font-weight: 600;
  margin-right: 20px;
  padding-bottom: 4px;
}

.tabLink:focus,
.tabLink:hover {
  color: gradientDark2;
  text-decoration: none !important; /* overriding legacy <a> styles */
}

.tabLinkActive {
  border-bottom: 2px solid var(--jcPrimary);
  font-weight: bold;
}

.contentContainer {
  border-top: 1px solid gradientLight3;
  margin-top: 4px;
}

.json {
  border: none;
  border-radius: 0;
  font-family: var(--jc-font-monospace);
  font-size: 10px;
}

.jsonBlock {
  margin: 10px 30px 30px;
}

.jsonInline {
  background: gradientLight2;
  font-family: var(--jc-font-monospace);
  font-size: 10px;
  max-height: 96px;
  overflow-x: hidden;
  overflow-y: scroll;
  padding: 5px;
  text-overflow: ellipsis;
}

.viewMoreButton {
  border-bottom: none !important;
  font-size: 12px;
}
</style>
